import { Plug } from '@plone/volto/components/manage/Pluggable';
import { updateContent } from '@plone/volto/actions';
import {
  FormattedDate,
  Icon,
  Toast,
  Toolbar,
  UniversalLink,
} from '@plone/volto/components';
import {
  flattenToAppURL,
  getBaseUrl,
  Helmet,
  tryParseJSON,
  usePrevious,
} from '@plone/volto/helpers';
import aheadSVG from '@plone/volto/icons/ahead.svg';
import backSVG from '@plone/volto/icons/back.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import rightArrowSVG from '@plone/volto/icons/right-key.svg';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  Checkbox,
  Container,
  Form,
  Segment,
  Table,
} from 'semantic-ui-react';
import { getReplicas } from '../../actions';

const messages = defineMessages({
  back: {
    id: 'Back',
    defaultMessage: 'Back',
  },
  managereplicacontent: {
    id: 'managereplicacontent',
    defaultMessage: 'Manage Replica Contents',
  },
  edit: {
    id: 'Edit {title}',
    defaultMessage: 'Edit {title}',
  },
  save: {
    id: 'Save',
    defaultMessage: 'Save',
  },
  cancel: {
    id: 'Cancel',
    defaultMessage: 'Cancel',
  },
  error: {
    id: 'Error',
    defaultMessage: 'Error',
  },
  someErrors: {
    id: 'There are some errors.',
    defaultMessage: 'There are some errors.',
  },
  department: {
    id: 'department',
    defaultMessage: 'Department',
  },
  site: {
    id: 'site',
    defaultMessage: 'Site',
  },
  replica: {
    id: 'replica',
    defaultMessage: 'Replica Content',
  },
  info: {
    id: 'info',
    defaultMessage: 'Info',
  },
  missing_subfolder: {
    id: 'missing_subfolder',
    defaultMessage: 'Missing subfolder',
  },
});

export const ReplicaMenu = (props) => {
  const { content, pathname, intl } = props;
  const path = getBaseUrl(pathname);
  return (
    content?.subsites && (
      <Plug pluggable="toolbar-more-manage-content" id="replica-menu">
        <li>
          <Link to={`${path}/@replica`}>
            <div>
              <span className="pastanaga-menu-label">
                {intl.formatMessage(messages.managereplicacontent)}
              </span>
              <span className="pastanaga-menu-value" />
            </div>
            <Icon name={rightArrowSVG} size="24px" />
          </Link>
        </li>
      </Plug>
    )
  );
};

const Replica = (props) => {
  const { location, history } = props;
  const pathname = location.pathname;
  const intl = useIntl();
  const dispatch = useDispatch();
  const replicaItems = useSelector((state) => state.replicaItems?.result);
  const [isClient, setIsClient] = useState(false);
  const content = useSelector((state) => state.content?.data, shallowEqual);
  const updateRequest = useSelector((state) => state.content.update);
  const prevrequestloading = usePrevious(updateRequest.loading);
  const [selectedContainers, setSelectedContainers] = useState([]);

  const onChange = (event, { checked, value }) => {
    setSelectedContainers(
      checked
        ? selectedContainers.concat([value])
        : selectedContainers.filter((uid) => uid !== value),
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    dispatch(
      updateContent(flattenToAppURL(getBaseUrl(pathname)), {
        subsites: selectedContainers,
      }),
    );
  };

  const onCancel = (e) => {
    e.preventDefault();
    history.push(getBaseUrl(pathname));
  };

  // useEffect(() => {
  //   getContent(getBaseUrl(pathname));
  // }, [pathname]);

  useEffect(() => {
    dispatch(getReplicas(flattenToAppURL(getBaseUrl(pathname))));
    setIsClient(true);
  }, []);

  useEffect(() => {
    setSelectedContainers(
      replicaItems
        .filter((item) => item.item?.UID)
        .map((item) => item.container.UID),
    );
  }, [replicaItems]);

  useEffect(() => {
    if (prevrequestloading && updateRequest.loaded) {
      dispatch(getReplicas(flattenToAppURL(getBaseUrl(pathname))));
    }
  }, [prevrequestloading, updateRequest.loaded]);

  useEffect(() => {
    if (updateRequest.error) {
      const message =
        updateRequest.error?.response?.body?.error?.message ||
        updateRequest.error?.response?.body?.message ||
        updateRequest.error?.response?.text ||
        '';
      const error =
        new DOMParser().parseFromString(message, 'text/html')?.all[0]
          ?.textContent || message;
      const errorsList = tryParseJSON(error);
      let erroMessage;
      if (Array.isArray(errorsList)) {
        const invariantErrors = errorsList
          .filter((errorItem) => !('field' in errorItem))
          .map((errorItem) => errorItem['message']);
        if (invariantErrors.length > 0) {
          // Plone invariant validation message.
          erroMessage = invariantErrors.join(' - ');
        } else {
          // Error in specific field.
          erroMessage = intl.formatMessage(messages.someErrors);
        }
      } else {
        erroMessage = error;
      }
      toast.error(
        <Toast
          error
          title={intl.formatMessage(messages.error)}
          content={erroMessage}
        />,
      );
    }
  }, [updateRequest.error]);

  return (
    <>
      <Container id="page-replica">
        <Helmet
          title={`${intl.formatMessage(messages.managereplicacontent)}: ${
            content?.title
          }`}
        />
        <Segment.Group raised>
          <Segment className="primary">
            <FormattedMessage
              id="Manage Replica Content for {title}"
              defaultMessage="Manage Replica Content for {title}"
              values={{ title: <q>{content?.title}</q> }}
            />
          </Segment>

          <Segment className="secondary">
            <h4>{content?.title}</h4>
            <p>{content?.description}</p>
            <p>
              <strong>{intl.formatMessage({ id: 'Last modified' })}:</strong>{' '}
              {content?.modified && (
                <FormattedDate includeTime date={content.modified} />
              )}
            </p>
          </Segment>
          <Form onSubmit={onSubmit}>
            <Table celled padded striped attached>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>
                    <Checkbox
                      onChange={(e, { checked }) => {
                        if (checked) {
                          setSelectedContainers(
                            replicaItems
                              .filter((item) => item.has_subfolder)
                              .map((item) => item.container.UID),
                          );
                          console.log(
                            replicaItems.map((item) => item.container.UID),
                          );
                        } else {
                          setSelectedContainers([]);
                        }
                      }}
                      label={intl.formatMessage(messages.site)}
                    />
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {intl.formatMessage(messages.department)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {intl.formatMessage(messages.replica)}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {intl.formatMessage({ id: 'Last modified' })}
                  </Table.HeaderCell>
                  <Table.HeaderCell>
                    {intl.formatMessage(messages.info)}
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {replicaItems &&
                  replicaItems.map((item) => (
                    <Table.Row key={item.container['@id']}>
                      <Table.Cell>
                        {item.has_subfolder ? (
                          <Checkbox
                            // defaultChecked={item.item ? true : false}
                            checked={
                              selectedContainers.indexOf(item.container.UID) >=
                              0
                            }
                            value={item.container.UID}
                            onChange={onChange}
                            label={item.container.title}
                          />
                        ) : (
                          item.container.title
                        )}
                        <br />
                        <a
                          href={item.container['@id']}
                          className={`state-${item.container.review_state}`}
                          target="_blank"
                        >
                          {item.container.id}
                        </a>
                        {/* <UniversalLink href={item.container['@id']}><Icon name={docSVG} alt={item.container.title}/></UniversalLink> */}
                      </Table.Cell>
                      <Table.Cell>
                        {item.container.department
                          ? item.container.department
                              .map((i) => i.title)
                              .join(', ')
                          : '-'}
                      </Table.Cell>
                      <Table.Cell>
                        {item.item && item.item['@id'] ? (
                          <UniversalLink
                            target="_blank"
                            href={item.item['@id']}
                          >
                            {item.item.title}
                          </UniversalLink>
                        ) : (
                          <span>{item.item && item.item.title}</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {item.item &&
                          item.item['@id'] &&
                          item.item?.modified && (
                            <FormattedDate
                              includeTime
                              date={item.item?.modified}
                            />
                          )}
                        {/* <FormattedDate long value={item.item?.modified} />} */}
                      </Table.Cell>
                      <Table.Cell>
                        {!item.has_subfolder &&
                          intl.formatMessage(messages.missing_subfolder)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table>
            <Segment className="actions" attached clearing>
              <Button
                basic
                primary
                floated="right"
                type="submit"
                aria-label={intl.formatMessage(messages.save)}
                title={intl.formatMessage(messages.save)}
                loading={updateRequest.loading}
                onClick={onSubmit}
              >
                <Icon className="circled" name={aheadSVG} size="30px" />
              </Button>
              <Button
                basic
                secondary
                aria-label={intl.formatMessage(messages.cancel)}
                title={intl.formatMessage(messages.cancel)}
                floated="right"
                onClick={onCancel}
              >
                <Icon className="circled" name={clearSVG} size="30px" />
              </Button>
            </Segment>
          </Form>
        </Segment.Group>
      </Container>
      {isClient &&
        pathname &&
        createPortal(
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <Link to={`${getBaseUrl(pathname)}`} className="item">
                <Icon
                  name={backSVG}
                  className="contents circled"
                  size="30px"
                  title={intl.formatMessage(messages.back)}
                />
              </Link>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </>
  );
};

// export default compose(
//   injectIntl,
//   connect((state, props) => {
//     return {
//       pathname: props.location.pathname,
//       // content: state.content?.data,
//     };
//   }),
// )(Replica);

export default Replica;
